import {
	CURRENCY_LABELS,
	Currency,
	type SupportedLanguage,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Building2, Coins, Globe, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchUserOrganizationsQueryOptions,
	useUpdateOrganization,
} from "@/api/organizations";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useApiParameters } from "@/hooks/useApiParameters";
import { languages } from "@/i18n";
import { OrganizationSettingsNavigation } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/OrganizationSettingsNavigation";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/settings/")({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		return { userId, token };
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const { orgId } = Route.useParams();
	// Fetch user organizations to get the current organization details
	const { data: organizations } = useSuspenseQuery(
		fetchUserOrganizationsQueryOptions(userId, token),
	);

	// Find the current organization
	const currentOrg = organizations.find((org) => org.id === orgId);

	// State for language selection
	const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(
		(currentOrg?.defaultLanguage as SupportedLanguage) || "en_US",
	);

	// State for currency selection
	const [selectedCurrency, setSelectedCurrency] = useState<Currency>(
		(currentOrg?.defaultCurrency as Currency) || Currency.BRL,
	);

	// Update local state when organization data changes
	useEffect(() => {
		if (currentOrg?.defaultLanguage) {
			setSelectedLanguage(currentOrg.defaultLanguage as SupportedLanguage);
		}
	}, [currentOrg?.defaultLanguage]);

	// Update currency state when organization data changes
	useEffect(() => {
		if (currentOrg?.defaultCurrency) {
			setSelectedCurrency(currentOrg.defaultCurrency as Currency);
		}
	}, [currentOrg?.defaultCurrency]);

	const updateOrganizationMutation = useUpdateOrganization();

	const handleLanguageChange = (value: string) => {
		const newLanguage = value as SupportedLanguage;
		setSelectedLanguage(newLanguage);

		// Save immediately on change
		updateOrganizationMutation.mutate({
			token,
			organizationId: orgId,
			data: {
				defaultLanguage: newLanguage,
			},
		});
	};

	const handleCurrencyChange = (value: string) => {
		const newCurrency = value as Currency;
		setSelectedCurrency(newCurrency);

		updateOrganizationMutation.mutate({
			token,
			organizationId: orgId,
			data: {
				defaultCurrency: newCurrency,
			},
		});
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgSettings.title")}
				description={t("orgSettings.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>

			{/* Main content area with side navigation */}
			<div
				className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="organization-settings-container"
			>
				{/* Side Navigation */}
				<OrganizationSettingsNavigation activeTab="general" />

				{/* Main Content Area */}
				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="organization-settings-main-content-area"
				>
					<div className="space-y-3 md:space-y-4">
						{/* Organization Details Card */}
						<Card>
							<CardContent className="py-3 md:py-4 px-3 md:px-6">
								<div className="space-y-4 md:space-y-6">
									{/* Organization Details */}
									<div className="space-y-3 md:space-y-4">
										<div className="flex items-center space-x-2 md:space-x-3">
											<div className="w-10 h-10 md:w-16 md:h-16 rounded-full bg-base-300 flex items-center justify-center flex-shrink-0">
												<Building2 className="w-5 h-5 md:w-8 md:h-8 text-base-content/60" />
											</div>
											<div className="flex-1 min-w-0">
												<h3 className="text-base md:text-lg font-semibold text-base-content truncate">
													{currentOrg?.name || t("orgSettings.title")}
												</h3>
												{currentOrg?.description && (
													<p className="text-xs md:text-sm text-base-content/70 line-clamp-2">
														{currentOrg.description}
													</p>
												)}
											</div>
										</div>
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Language Settings Card */}
						<Card>
							<CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
								<CardTitle className="flex items-center gap-2 text-sm md:text-base">
									<Globe className="w-4 h-4 md:w-5 md:h-5" />
									{t("orgSettings.language.title")}
								</CardTitle>
							</CardHeader>
							<CardContent className="px-3 md:px-6 pb-3 md:pb-6">
								<div className="space-y-3 md:space-y-4">
									<p className="text-xs md:text-sm text-base-content/70">
										{t("orgSettings.language.description")}
									</p>

									<div className="space-y-2">
										<Label
											htmlFor="default-language"
											className="text-xs md:text-sm"
										>
											{t("orgSettings.language.defaultLanguage")}
										</Label>
										<div className="flex items-center gap-2">
											<Select
												value={selectedLanguage}
												onValueChange={handleLanguageChange}
												disabled={updateOrganizationMutation.isPending}
											>
												<SelectTrigger
													className="w-full md:w-[200px]"
													id="default-language"
												>
													<SelectValue
														placeholder={t(
															"orgSettings.language.selectLanguage",
														)}
													/>
												</SelectTrigger>
												<SelectContent>
													{Object.entries(languages).map(([key, lang]) => (
														<SelectItem key={key} value={key}>
															<span className="flex items-center gap-2">
																<span>{lang.flag}</span>
																<span>{lang.nativeName}</span>
															</span>
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{updateOrganizationMutation.isPending && (
												<Loader2 className="w-4 h-4 animate-spin text-base-content/50" />
											)}
										</div>
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Currency Settings Card */}
						<Card>
							<CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
								<CardTitle className="flex items-center gap-2 text-sm md:text-base">
									<Coins className="w-4 h-4 md:w-5 md:h-5" />
									{t("orgSettings.currency.title")}
								</CardTitle>
							</CardHeader>
							<CardContent className="px-3 md:px-6 pb-3 md:pb-6">
								<div className="space-y-3 md:space-y-4">
									<p className="text-xs md:text-sm text-base-content/70">
										{t("orgSettings.currency.description")}
									</p>

									<div className="space-y-2">
										<Label
											htmlFor="default-currency"
											className="text-xs md:text-sm"
										>
											{t("orgSettings.currency.defaultCurrency")}
										</Label>
										<div className="flex items-center gap-2">
											<Select
												value={selectedCurrency}
												onValueChange={handleCurrencyChange}
												disabled={updateOrganizationMutation.isPending}
											>
												<SelectTrigger
													className="w-full md:w-[200px]"
													id="default-currency"
												>
													<SelectValue
														placeholder={t(
															"orgSettings.currency.selectCurrency",
														)}
													/>
												</SelectTrigger>
												<SelectContent>
													{Object.entries(CURRENCY_LABELS).map(
														([currency, label]) => (
															<SelectItem key={currency} value={currency}>
																{label}
															</SelectItem>
														),
													)}
												</SelectContent>
											</Select>
											{updateOrganizationMutation.isPending && (
												<Loader2 className="w-4 h-4 animate-spin text-base-content/50" />
											)}
										</div>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</div>
	);
}
